import { ImpulseElement, property, registerElement, target, targets } from '@ambiki/impulse';
import type AwcDialogElement from '@ambiki/impulse-view-components/dist/elements/dialog';
import { createHiddenInput } from 'helpers/form';

@registerElement('patient-attendance')
export default class PatientAttendanceElement extends ImpulseElement {
  /**
   * Whether the element is active or not. If active, then attendance can be marked.
   */
  @property({ type: Boolean }) active = false;

  /**
   * An array of patient ids that were selected from the checkboxes.
   */
  @property({ type: Array }) selectedPatientIds: string[] = [];

  /**
   * The form name of the attendance type.
   */
  @property() attendanceTypeIdName: string;

  /**
   * The form name of the patient ids.
   */
  @property() patientIdsName: string;

  /**
   * JSON for all attendance types.
   */
  @property({ type: Array }) attendanceTypes: Array<{ id: string; name: string }>;

  /**
   * DOM ID for the attendance dialog.
   */
  @property() randomId: string;

  /**
   * Current attendance type for the selected patients
   * Set when a single patient is selected or when all selected patients have the same attendance
   */
  @property() currentAttendanceTypeId: string | null = null;

  @target() dialog: AwcDialogElement;
  @target() dropdown: HTMLElement;
  @target() button: HTMLButtonElement;
  @target() form: HTMLFormElement;
  @target() presentForm?: HTMLFormElement;
  @target() clearForm: HTMLFormElement;
  @target() clearFormWrapper: HTMLElement;
  @target() sameReasonContainer: HTMLElement;
  @target() sameReasonTextareaContainer: HTMLElement;
  @targets() textareaContainers: HTMLElement[];

  constructor() {
    super();
    this.handleDropdownShown = this.handleDropdownShown.bind(this);
    this.handleDropdownHidden = this.handleDropdownHidden.bind(this);
    this.beforeMark = this.beforeMark.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   */
  connected() {
    // Safety check for dropdown existence before attaching events
    if (this.dropdown) {
      $(this.dropdown).on('shown.bs.dropdown', this.handleDropdownShown);
      $(this.dropdown).on('hidden.bs.dropdown', this.handleDropdownHidden);
    }

    // Safety check for button before setting disabled state
    if (this.button) {
      this.button.disabled = !this.active;
    }

    // Add direct event listeners for ajax:before events with null checks
    if (this.presentForm) {
      this.presentForm.addEventListener('ajax:before', this.beforeMark);

      const submitButton = this.presentForm.querySelector('input[type="submit"]');
      if (submitButton) {
        submitButton.removeAttribute('data-disable-with');
      }
    }

    if (this.clearForm) {
      this.clearForm.addEventListener('ajax:before', this.beforeMark);
    }

    if (this.form) {
      this.form.addEventListener('ajax:before', this.beforeMark);
    }

    // Initialize the current attendance state
    this.updateCurrentAttendanceState();

    // If we have a clearFormWrapper, initially hide it if no attendance
    if (this.clearFormWrapper && this.selectedPatientIds.length > 0) {
      this.clearFormWrapper.hidden = !this.hasAnyPatientAttendance();
    }
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnected() {
    // Safety check for dropdown existence before removing events
    if (this.dropdown) {
      $(this.dropdown).off('shown.bs.dropdown', this.handleDropdownShown);
      $(this.dropdown).off('hidden.bs.dropdown', this.handleDropdownHidden);
    }

    // Clean up event listeners
    if (this.presentForm) {
      this.presentForm.removeEventListener('ajax:before', this.beforeMark);
    }

    if (this.clearForm) {
      this.clearForm.removeEventListener('ajax:before', this.beforeMark);
    }

    if (this.form) {
      this.form.removeEventListener('ajax:before', this.beforeMark);
    }
  }

  /**
   * Called when the `active` property changes it's state.
   */
  activeChanged(active: boolean) {
    // Safety check for button before setting disabled state
    if (this.button) {
      this.button.disabled = !active;
    }
  }

  /**
   * Called when the selectedPatientIds property changes
   */
  selectedPatientIdsChanged(newIds: string[]) {
    // Update the current attendance state based on new selection
    this.updateCurrentAttendanceState();

    // Update Clear button visibility if wrapper exists
    if (this.clearFormWrapper && newIds.length > 0) {
      this.clearFormWrapper.hidden = !this.hasAnyPatientAttendance();
    }
  }

  /**
   * Checks if any selected patient has attendance
   * Returns true if at least one patient has attendance, false otherwise
   */
  private hasAnyPatientAttendance(): boolean {
    if (!this.selectedPatientIds || this.selectedPatientIds.length === 0) {
      return false;
    }

    for (const patientId of this.selectedPatientIds) {
      const attendanceInfo = this.getPatientAttendanceInfo(patientId);
      if (attendanceInfo) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the attendance information for a patient
   * Returns { typeId, typeName } if found, or null if not found
   */
  private getPatientAttendanceInfo(patientId: string): { typeId: string; typeName: string } | null {
    const patientElement = document.querySelector(`li[data-patient-id="${patientId}"]`);
    if (!patientElement) return null;

    // Try to find attendance text in the patient element
    const attendanceSpan = patientElement.querySelector('span.text-muted');
    if (!attendanceSpan || !attendanceSpan.textContent) {
      return null;
    }

    // Extract attendance type from the text (format is typically "Type: Reason" or just "Type")
    const attendanceText = attendanceSpan.textContent.split(':')[0].trim();

    // Find matching attendance type
    const matchingType = this.attendanceTypes?.find(type => type.name === attendanceText);
    if (!matchingType) return null;

    return {
      typeId: matchingType.id,
      typeName: matchingType.name
    };
  }

  /**
   * Updates the current attendance state based on selected patients
   * - If one patient is selected, shows their attendance (if any)
   * - If multiple patients are selected and all have the same attendance, shows that attendance
   * - If multiple patients are selected with different attendance types, shows no selection
   */
  updateCurrentAttendanceState() {
    // Reset current attendance type
    this.currentAttendanceTypeId = null;

    // If no patients are selected, return early
    if (!this.selectedPatientIds || this.selectedPatientIds.length === 0) {
      return;
    }

    // Get attendance for first patient
    const firstPatientId = this.selectedPatientIds[0];
    const firstAttendance = this.getPatientAttendanceInfo(firstPatientId);

    // If first patient has no attendance, we're done
    if (!firstAttendance) {
      return;
    }

    // If only one patient is selected, use their attendance
    if (this.selectedPatientIds.length === 1) {
      this.currentAttendanceTypeId = firstAttendance.typeId;
      return;
    }

    // For multiple patients, check if all have the same attendance
    let allMatch = true;

    // Skip first patient (already checked)
    for (let i = 1; i < this.selectedPatientIds.length; i++) {
      const patientId = this.selectedPatientIds[i];
      const attendance = this.getPatientAttendanceInfo(patientId);

      // If this patient has no attendance or different attendance, they don't all match
      if (!attendance || attendance.typeId !== firstAttendance.typeId) {
        allMatch = false;
        break;
      }
    }

    // If all patients have the same attendance, use that
    if (allMatch) {
      this.currentAttendanceTypeId = firstAttendance.typeId;
    }
  }

  /**
   * Update the dropdown to show the currently selected option
   */
  updateDropdownUI() {
    if (!this.dropdown) return;

    // Find all dropdown items (buttons and submit inputs)
    const dropdownItems = this.dropdown.querySelectorAll('.dropdown-item');

    // Remove active class from all items
    dropdownItems.forEach(item => {
      item.classList.remove('active');
    });

    // If no current attendance type, we're done
    if (!this.currentAttendanceTypeId) {
      return;
    }

    // Find the button with matching attendance type id and mark it active
    const matchingButton = this.dropdown.querySelector(
      `button[data-attendance-type-id="${this.currentAttendanceTypeId}"]`
    );

    if (matchingButton) {
      matchingButton.classList.add('active');
    } else {
      // Handle the case where it might be "Present" which is a submit button
      const presentForm = this.presentForm;
      if (presentForm) {
        const presentType = this.attendanceTypes?.find(type => type.name === "Present");
        if (presentType && presentType.id === this.currentAttendanceTypeId) {
          const presentButton = presentForm.querySelector('input[type="submit"]');
          if (presentButton) {
            presentButton.classList.add('active');
          }
        }
      }
    }
  }

  /**
   * Get the total count of patients in the form
   */
  private getTotalPatientCount(): number {
    const allCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]');
    return allCheckboxes.length;
  }

  /**
   * Check if all patients are selected
   */
  private isAllPatientsSelected(): boolean {
    const allCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]');
    const selectedCheckboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]:checked');

    return allCheckboxes.length > 0 && allCheckboxes.length === selectedCheckboxes.length;
  }

  /**
   * Directly uncheck the "check all" box
   */
  private uncheckedCheckAllBox() {
    // Target the specific checkbox from your implementation
    const specificCheckAllBox = document.querySelector('input[data-checkbox-select-all-target="checkboxAll"]') as HTMLInputElement;
    if (specificCheckAllBox && specificCheckAllBox.checked) {
      specificCheckAllBox.checked = false;

      // Dispatch a change event to ensure any listeners update
      const changeEvent = new Event('change', { bubbles: true });
      specificCheckAllBox.dispatchEvent(changeEvent);
      return;
    }

    // Fallback to common selectors for "check all" checkboxes if the specific one isn't found
    const checkAllSelectors = [
      'input[data-action="patient-list#toggleAll"]',
      'input[data-action="change->checkbox-select-all#toggle"]',
      'input.check-all',
      'input.select-all',
      'input.custom-control-input[id$="-patient-list"]',
      'input[id="select_all"]',
      'input[id="check_all"]',
      'input[name="select_all"]',
      'input[name="check_all"]',
      'input[data-check-all="true"]',
      'input[data-select-all="true"]'
    ];

    // Try each selector and directly uncheck the first matching element we find
    for (const selector of checkAllSelectors) {
      const checkAllBox = document.querySelector(selector) as HTMLInputElement;
      if (checkAllBox && checkAllBox.checked) {
        checkAllBox.checked = false;

        // Also dispatch a change event on this checkbox to ensure any listeners update
        const changeEvent = new Event('change', { bubbles: true });
        checkAllBox.dispatchEvent(changeEvent);

        // If we found and updated one, we can exit
        return;
      }
    }
  }

  /**
   * Clear all checkboxes and update related components
   */
  private clearCheckboxes() {
    // Clear individual patient checkboxes
    const checkboxes = document.querySelectorAll('input[data-target="patient-list.checkboxes"]:checked');
    checkboxes.forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = false;
    });

    // Clear any "check all" checkbox
    document.querySelectorAll('input[data-checkbox-select-all-target]').forEach(box => {
      const input = box as HTMLInputElement;
      if (input.checked || input.indeterminate) {
        input.checked = false;
        input.indeterminate = false;
      }
    });

    // Reset the selected patient ids
    this.selectedPatientIds = [];

    // Update the patient-checkin component if it exists
    const patientCheckinElement = document.querySelector('patient-checkin[active]');
    if (patientCheckinElement) {
      // Remove the active attribute
      patientCheckinElement.removeAttribute('active');

      // Also update the selected-patient-ids attribute to an empty array
      patientCheckinElement.setAttribute('selected-patient-ids', '[]');

      // If this component has a button like our attendance component
      const checkinButton = patientCheckinElement.querySelector('button');
      if (checkinButton) {
        checkinButton.disabled = true;
      }
    }

    // Trigger any potential change events to ensure UI consistency
    this.dispatchCheckboxChangeEvents();
  }

  /**
   * Dispatch change events to ensure UI state is consistent
   */
  private dispatchCheckboxChangeEvents() {
    // Find any checkbox controllers that might need to be notified
    const checkboxControllers = document.querySelectorAll('[data-controller*="patient-list"]');
    if (checkboxControllers.length > 0) {
      // Create a change event
      const changeEvent = new Event('change', { bubbles: true });

      // Dispatch the event on the first controller to trigger any necessary UI updates
      checkboxControllers[0].dispatchEvent(changeEvent);
    }
  }

  /**
   * Called right before the AJAX goes out.
   * and update the attendance information under the patient name.
   */
  beforeMark(event) {
    const isAllPatientsSelected = this.isAllPatientsSelected();
    const totalPatientCount = this.getTotalPatientCount();

    // Close the dialog/modal if it exists
    if (this.dialog) {
      this.dialog.hideWithoutEmitting();
    }

    // Make sure dropdown exists before trying to hide it
    if (this.dropdown) {
      $(this.dropdown).dropdown('hide');
    }

    // Update the attendance information under patient names
    this.updatePatientAttendanceUI(event);

    // Clear checkboxes if there is more than 1 patient in the form, regardless of how many are selected
    if (totalPatientCount > 1) {
      // First, directly unchecked the "check all" box if all patients were selected
      if (isAllPatientsSelected) {
        this.uncheckedCheckAllBox();
      }

      // Then clear all patient checkboxes
      this.clearCheckboxes();
    }

    // Update current attendance type based on the form that was submitted
    // We'll update it fully after a brief timeout to allow DOM to update
    setTimeout(() => {
      this.updateCurrentAttendanceState();
    }, 100);
  }

  /**
   * Updates the UI to show attendance information under patient names
   * Preserves alignment classes when updating the DOM
   */
  updatePatientAttendanceUI(event) {
    // Get the selected patients
    const selectedPatientIds = this.selectedPatientIds;
    if (!selectedPatientIds || selectedPatientIds.length === 0) {
      return;
    }

    // Get the attendance type with safety check
    const attendanceTypeIdElement = this.querySelector(`[name="${this.attendanceTypeIdName}"]`);
    let attendanceTypeId = attendanceTypeIdElement ? attendanceTypeIdElement.value : null;
    let attendanceTypeName = null;

    // Safe check for attendance type ID
    if (attendanceTypeId) {
      attendanceTypeName = this.getAttendanceTypeNameById(attendanceTypeId);
      if (!attendanceTypeName) {
        return;
      }
    }

    // Special case for "Present" from the Present form
    if ((!attendanceTypeId || !attendanceTypeName) && this.presentForm && event && event.target === this.presentForm) {
      const presentType = this.attendanceTypes?.find(type => type.name === "Present");
      if (presentType) {
        attendanceTypeName = "Present";
        attendanceTypeId = presentType.id;
      } else {
        return;
      }
    }

    // Handle the case where we still don't have an attendance type and it's not a clear operation
    if (!attendanceTypeName && (!event || event.target !== this.clearForm)) {
      return;
    }

    // For each selected patient, update their attendance UI
    for (const patientId of selectedPatientIds) {
      // Find the patient element using the data-patient-id attribute
      const patientListItem = document.querySelector(`li[data-patient-id="${patientId}"]`);

      if (!patientListItem) {
        continue;
      }

      // Extract all the elements we need to work with
      // First find the outermost flex container
      const outerFlexContainer = patientListItem.querySelector('.flex-grow-1.d-flex');
      if (!outerFlexContainer) {
        continue;
      }

      // Find the second-level flex container - could be align-items-start or align-items-center
      // Based on the ERB template structure
      const innerFlexContainers = outerFlexContainer.querySelectorAll('.flex-grow-1.d-flex');
      let innerFlexContainer = innerFlexContainers.length > 0 ? innerFlexContainers[0] : null;

      if (!innerFlexContainer) {
        // Alternative approach - get any direct child that has d-flex
        const allInnerContainers = outerFlexContainer.children;
        for (const child of allInnerContainers) {
          if (child.classList.contains('d-flex')) {
            innerFlexContainer = child;
            break;
          }
        }
      }

      if (!innerFlexContainer) {
        continue;
      }

      // Find the ML-2 container which should contain our target
      const ml2Container = outerFlexContainer.querySelector('.ml-2');
      if (!ml2Container) {
        continue;
      }

      // Now look for a d-flex inside the ml-2 container
      let nameContainer = ml2Container.querySelector('.d-flex');
      if (!nameContainer) {
        continue;
      }

      // Handle alignment classes based on whether we're adding or removing attendance
      const isClearing = event && event.target === this.clearForm;

      // 1. Update outer container alignment
      if (isClearing) {
        outerFlexContainer.classList.remove('align-items-start');
        outerFlexContainer.classList.add('align-items-center');
      } else {
        outerFlexContainer.classList.remove('align-items-center');
        outerFlexContainer.classList.add('align-items-start');
      }

      // 2. Update inner container alignment if it exists
      if (innerFlexContainer && innerFlexContainer !== outerFlexContainer) {
        if (isClearing) {
          innerFlexContainer.classList.remove('align-items-start');
          innerFlexContainer.classList.add('align-items-center');
        } else {
          innerFlexContainer.classList.remove('align-items-center');
          innerFlexContainer.classList.add('align-items-start');
        }
      }

      // Check if name container already has flex-column
      const hasFlexColumn = nameContainer.classList.contains('flex-column');

      // If it doesn't have flex-column and we're not clearing, add it
      if (!hasFlexColumn && !isClearing) {
        nameContainer.classList.add('flex-column', 'align-items-start');
      }

      // Find existing attendance span if it exists
      let attendanceSpan = nameContainer.querySelector('span.text-muted');

      // If this is a "Clear" submission
      if (isClearing) {
        // Remove attendance span if it exists
        if (attendanceSpan) {
          attendanceSpan.remove();
        }

        // If name container has flex-column and we're clearing, remove it
        if (hasFlexColumn) {
          nameContainer.classList.remove('flex-column', 'align-items-start');
        }

        continue;
      }

      // Create or update attendance span
      if (!attendanceSpan) {
        attendanceSpan = document.createElement('span');
        attendanceSpan.className = 'text-muted';
        attendanceSpan.style.fontSize = '12px';
        attendanceSpan.style.lineHeight = '18px';
        nameContainer.appendChild(attendanceSpan);
      }

      // Update the span content
      // If this is from the reason form, get the reason text
      let reason = '';
      if (this.form && event && this.form === event.target) {
        const reasonField = document.getElementById(`reason-${patientId}-${this.randomId}`);
        const sameReasonCheckbox = document.getElementById(`${this.randomId}-same-reason`);

        if (sameReasonCheckbox && (sameReasonCheckbox as HTMLInputElement).checked) {
          // Using the same reason for all patients
          const cumulativeReasonField = document.getElementById(`cumulative-reason-${this.randomId}`);
          reason = cumulativeReasonField ? (cumulativeReasonField as HTMLTextAreaElement).value || '' : '';
        } else if (reasonField) {
          // Individual reason
          reason = (reasonField as HTMLTextAreaElement).value || '';
        }
      }

      // Update the span text
      const finalText = reason ? `${attendanceTypeName}: ${reason}` : attendanceTypeName;
      attendanceSpan.textContent = finalText;
    }
  }

  /**
   * Called when the dialog is shown. This is a great place to initialize things.
   */
  dialogShown(event: CustomEvent<{ dialog: AwcDialogElement; relatedTarget: HTMLButtonElement }>) {
    const invoker = event.detail.relatedTarget;
    if (!invoker) return;

    const attendanceTypeId = invoker.getAttribute('data-attendance-type-id');
    if (!attendanceTypeId) return;

    this.updateDialogTitle(attendanceTypeId);

    if (this.form) {
      const hiddenInput = createHiddenInput(this.attendanceTypeIdName, attendanceTypeId);
      this.form.append(hiddenInput);
    }

    if (this.sameReasonContainer) {
      this.sameReasonContainer.hidden = this.selectedPatientIds.length === 1;
    }

    this.showPatientTextareas();
  }

  /**
   * Called when the dialog is hidden. Clean up task can be performed here.
   */
  dialogHidden() {
    this.resetForm();
    this.removeAttendanceIds();
    this.removePatientIds();
  }

  /**
   * Update the title of the attendance dialog to indicate the attendance type.
   */
  updateDialogTitle(attendanceTypeId: string) {
    const title = document.getElementById(`${this.randomId}_title`);
    if (!title) return;

    const attendanceName = this.getAttendanceTypeNameById(attendanceTypeId);
    if (attendanceName) {
      title.textContent = `${attendanceName} reason`;
    }
  }

  /**
   * Returns the attendance type name based on the attendance id.
   */
  getAttendanceTypeNameById(id: string): string | null {
    if (!this.attendanceTypes) return null;
    const attendanceType = this.attendanceTypes.find((at) => at && at.id === id);
    return attendanceType ? attendanceType.name : null;
  }

  /**
   * Called when the "Same reason" checkbox is toggled.
   */
  toggleSameReason({ target: checkbox }: Event) {
    if (!(checkbox instanceof HTMLInputElement)) return;

    if (checkbox.checked) {
      this.hidePatientTextareas();
      this.sameReasonTextAreaActive(true);
      this.appendPatientIds(this.form);
    } else {
      this.showPatientTextareas();
      this.sameReasonTextAreaActive(false);
      this.removePatientIds();
    }
  }

  /**
   * Called when the attendance has been marked successfully.
   */
  attendanceMarked(event: CustomEvent) {
    if (this.dialog) {
      this.dialog.hideWithoutEmitting();
    }

    if (this.dropdown) {
      $(this.dropdown).dropdown('hide');
    }

    this.emit('marked', { detail: event.detail[0] });
  }

  private handleDropdownShown() {
    if (this.presentForm) {
      this.appendPatientIds(this.presentForm);
    }

    if (this.clearForm && this.clearFormWrapper) {
      // Only show the clear option if at least one patient has attendance
      const shouldShowClear = this.hasAnyPatientAttendance();
      this.clearFormWrapper.hidden = !shouldShowClear;

      // Only append patient IDs if we're showing the clear option
      if (shouldShowClear) {
        this.appendPatientIds(this.clearForm);
      }
    }

    // Update dropdown UI to show active state
    this.updateDropdownUI();
  }

  private handleDropdownHidden() {
    this.removePatientIds();
  }

  private resetForm() {
    if (this.form) {
      this.form.reset();
    }
    this.sameReasonTextAreaActive(false);
  }

  /**
   * Shows the textarea of the patients that were selected from the checkboxes.
   */
  private showPatientTextareas() {
    for (const container of this.textareaContainers) {
      const textarea = container.querySelector('textarea');
      const patientId = container.dataset.patientId;
      if (!textarea || !patientId) continue;

      const isSelected = this.selectedPatientIds.includes(patientId);
      container.hidden = !isSelected;
      textarea.required = isSelected;
    }
  }

  private hidePatientTextareas() {
    for (const container of this.textareaContainers) {
      const textarea = container.querySelector('textarea');
      if (!textarea) continue;
      container.hidden = true;
      textarea.required = false;
    }
  }

  private sameReasonTextAreaActive(value: boolean) {
    if (!this.sameReasonTextareaContainer) return;

    const textarea = this.sameReasonTextareaContainer.querySelector('textarea');
    if (!textarea) return;

    this.sameReasonTextareaContainer.hidden = !value;
    textarea.required = value;
  }

  private appendPatientIds(form: HTMLFormElement) {
    if (!form) return;

    for (const id of this.selectedPatientIds) {
      const hiddenInput = createHiddenInput(this.patientIdsName, id);
      form.append(hiddenInput);
    }
  }

  private removeAttendanceIds() {
    const hiddenInputs = Array.from(this.querySelectorAll(`[name="${this.attendanceTypeIdName}"]`));
    hiddenInputs.forEach((input) => input.remove());
  }

  private removePatientIds() {
    const hiddenInputs = Array.from(this.querySelectorAll(`[name="${this.patientIdsName}"]`));
    hiddenInputs.forEach((input) => input.remove());
  }
}